import { collection, query, where, getDocs, doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { createFollowNotification } from './notificationService';

const usersCollection = collection(db, 'users');

export async function createUserProfile(uid, data) {
    const ref = doc(db, 'users', uid);
    const name = data.name || data.displayName || data.fullName || '';
    const merged = {
        ...data,
        name: name,
        nameLower: name ? name.toLowerCase() : '',
        createdAt: serverTimestamp()
    };
    await setDoc(ref, merged, { merge: true });
    return (await getDoc(ref)).data();
}

export async function getUserById(uid) {
    if (!uid) return null;
    const ref = doc(db, 'users', uid);
    const snap = await getDoc(ref);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function searchUsersByName(term, limit = 20) {
    if (!term || term.trim().length === 0) return [];
    const q = query(usersCollection, where('nameLower', '>=', term.toLowerCase()), where('nameLower', '<=', term.toLowerCase() + '\uf8ff'));
    const snaps = await getDocs(q);
    return snaps.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function followUser(currentUid, targetUid) {
    try {
        // Use nested subcollections for followers/following
        // Create document in target user's followers subcollection
        await setDoc(
            doc(db, "users", targetUid, "followers", currentUid),
            { followedAt: serverTimestamp() }
        );
        
        // Create document in current user's following subcollection
        await setDoc(
            doc(db, "users", currentUid, "following", targetUid),
            { followedAt: serverTimestamp() }
        );
        
        // Create notification for artist when someone follows them
        // Fire and forget - don't wait for notification creation
        createFollowNotification(currentUid, targetUid).catch(err => {
            console.error('Failed to create follow notification:', err);
        });
    } catch (error) {
        console.error('Error following user:', error);
        throw error;
    }
}

export async function unfollowUser(currentUid, targetUid) {
    try {
        // Delete document from target user's followers subcollection
        await deleteDoc(doc(db, "users", targetUid, "followers", currentUid));
        
        // Delete document from current user's following subcollection
        await deleteDoc(doc(db, "users", currentUid, "following", targetUid));
    } catch (error) {
        console.error('Error unfollowing user:', error);
        throw error;
    }
}

export async function isFollowing(currentUid, targetUid) {
    try {
        // Check if document exists in current user's following subcollection
        const followingRef = doc(db, "users", currentUid, "following", targetUid);
        const followingSnap = await getDoc(followingRef);
        return followingSnap.exists();
    } catch (error) {
        console.error('Error checking follow status:', error);
        return false;
    }
}

/**
 * Get list of user IDs that a user is following
 */
export async function getFollowingList(uid) {
    try {
        const followingCol = collection(db, "users", uid, "following");
        const snaps = await getDocs(followingCol);
        return snaps.docs.map(d => d.id);
    } catch (error) {
        console.error('Error getting following list:', error);
        return [];
    }
}

/**
 * Get list of user IDs that follow a user
 */
export async function getFollowersList(uid) {
    try {
        const followersCol = collection(db, "users", uid, "followers");
        const snaps = await getDocs(followersCol);
        return snaps.docs.map(d => d.id);
    } catch (error) {
        console.error('Error getting followers list:', error);
        return [];
    }
}

/**
 * Get count of users that a user is following
 */
export async function getFollowingCount(uid) {
    try {
        const followingCol = collection(db, "users", uid, "following");
        const snaps = await getDocs(followingCol);
        return snaps.size;
    } catch (error) {
        console.error('Error getting following count:', error);
        return 0;
    }
}

/**
 * Get count of users that follow a user
 */
export async function getFollowersCount(uid) {
    try {
        const followersCol = collection(db, "users", uid, "followers");
        const snaps = await getDocs(followersCol);
        return snaps.size;
    } catch (error) {
        console.error('Error getting followers count:', error);
        return 0;
    }
}
